import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { SeverityBadge } from '@/components/domain/SeverityBadge';
import { BaseMap, type MapMarker } from '@/components/map/BaseMap';
import { useCrashes, useDeviceStatus } from '@/features/shared/queries';
import { POLL } from '@/api/queryKeys';
import { coordinates } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { DeviceStatus } from '@/types/api';

type SortKey = 'serial' | 'status' | 'seen';

export function FleetPage() {
  const status = useDeviceStatus(POLL.fleetStatus);
  const crashes = useCrashes(15);
  const [sort, setSort] = useState<SortKey>('status');

  const devices = useMemo(() => {
    const list = [...(status.data ?? [])];
    list.sort((a, b) => {
      switch (sort) {
        case 'serial':
          return a.serial_number.localeCompare(b.serial_number);
        case 'seen':
          return (b.last_seen ?? '').localeCompare(a.last_seen ?? '');
        case 'status':
        default:
          // Devices that have gone quiet are the ones worth looking at.
          if (a.is_online !== b.is_online) return a.is_online ? 1 : -1;
          return (b.last_seen ?? '').localeCompare(a.last_seen ?? '');
      }
    });
    return list;
  }, [status.data, sort]);

  const positioned = devices.filter(
    (device): device is DeviceStatus & { gps_lat: number; gps_lon: number } =>
      device.gps_lat !== null && device.gps_lon !== null
  );

  const markers: MapMarker[] = positioned.map((device) => ({
    id: device.id,
    position: [device.gps_lat, device.gps_lon],
    title: device.serial_number,
    subtitle: device.is_online ? 'Reporting now' : 'Last known position',
  }));

  const offline = devices.filter((device) => !device.is_online).length;

  return (
    <>
      <PageHeader
        title="Fleet"
        description={
          status.isPending
            ? 'Loading device positions…'
            : `${devices.length} device${devices.length === 1 ? '' : 's'}${offline > 0 ? ` · ${offline} not reporting` : ' · all reporting'}`
        }
      />

      {status.isError ? (
        <ErrorState error={status.error} onRetry={status.refetch} />
      ) : (
        <>
          <Card className="mb-4">
            <CardHeader title="Positions" description="Refreshed every 15 seconds." />
            {status.isPending ? (
              <div className="p-4">
                <Skeleton className="h-96 w-full" />
              </div>
            ) : markers.length === 0 ? (
              <EmptyState
                icon={Truck}
                title="No positions reported"
                description="Devices appear on this map once they report a GPS fix."
              />
            ) : (
              <div className="p-4">
                <BaseMap markers={markers} height="h-96" />
              </div>
            )}
          </Card>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader
                title="Devices"
                action={
                  <select
                    value={sort}
                    onChange={(event) => setSort(event.target.value as SortKey)}
                    className="rounded-[--radius-control] border border-line-strong bg-surface px-2 py-1 text-[13px] text-ink"
                    aria-label="Sort devices"
                  >
                    <option value="status">Not reporting first</option>
                    <option value="seen">Most recently seen</option>
                    <option value="serial">Serial number</option>
                  </select>
                }
              />
              {status.isPending ? (
                <div className="p-4">
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : devices.length === 0 ? (
                <EmptyState
                  icon={Truck}
                  title="No devices in this fleet"
                  description="Devices appear here once they are claimed by their owners."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="border-b border-line text-left text-ink-muted">
                      <tr>
                        <th className="px-4 py-2 font-medium">Device</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Last seen</th>
                        <th className="hidden px-4 py-2 font-medium md:table-cell">Position</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {devices.map((device) => (
                        <tr
                          key={device.id}
                          className={cn(
                            'transition-colors hover:bg-surface-sunken',
                            !device.is_online && 'bg-warn-soft/40'
                          )}
                        >
                          <td className="px-4 py-2.5">
                            <Link
                              to={`/devices/${device.id}`}
                              className="font-medium text-accent hover:text-accent-hover"
                            >
                              {device.serial_number}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5">
                            <OnlineDot online={device.is_online} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5">
                            <RelativeTime value={device.last_seen} />
                          </td>
                          <td className="hidden px-4 py-2.5 text-ink-secondary md:table-cell" data-numeric>
                            {coordinates(device.gps_lat, device.gps_lon)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title="Recent crashes" />
              {crashes.isPending ? (
                <div className="p-4">
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : (crashes.data ?? []).length === 0 ? (
                <EmptyState
                  icon={Truck}
                  title="No crashes"
                  description="No impacts detected across the fleet."
                />
              ) : (
                <ul className="divide-y divide-line">
                  {(crashes.data ?? []).map((crash) => (
                    <li key={crash.id}>
                      <Link
                        to={`/crashes/${crash.id}`}
                        className="block px-4 py-3 transition-colors hover:bg-surface-sunken"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <SeverityBadge severity={crash.severity} />
                          <RelativeTime value={crash.timestamp} />
                        </div>
                        <p className="mt-1 text-[12px] text-ink-muted" data-numeric>
                          {coordinates(crash.gps_lat, crash.gps_lon)}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </>
  );
}
